import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate, useSearchParams } from "react-router-dom";

import {
  fetchChannelPartnerEarnings,
  fetchChannelPartnerMonthlyEarnings,
} from "../../services/channelPartnerPortalApi";
import ChannelPartnerDashboardLayout from "./ChannelPartnerDashboardLayout";

const DEFAULT_PAGE_SIZE = 20;

const formatCurrency = (value) => new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
}).format(Number(value || 0));

const formatMonth = (value) => {
  if (!value || !/^\d{4}-\d{2}$/.test(value)) return value || "All months";
  const [year, month] = value.split("-").map(Number);
  return new Intl.DateTimeFormat("en-IN", {
    month: "long",
    year: "numeric",
  }).format(new Date(year, month - 1, 1));
};

const formatDateTime = (value) => {
  if (!value) return "--";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "--";
  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const getErrorMessage = (error, fallback) => (
  error?.response?.data?.message || fallback
);

const ChannelPartnerEarningDetailsPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedMonth = searchParams.get("month") || "";
  const initialMonthRef = useRef(requestedMonth);
  const [months, setMonths] = useState([]);
  const [monthsLoading, setMonthsLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(requestedMonth);
  const [eventType, setEventType] = useState("");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [earnings, setEarnings] = useState([]);
  const [selectedTotal, setSelectedTotal] = useState(0);
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: DEFAULT_PAGE_SIZE,
    total_records: 0,
    total_pages: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    setMonthsLoading(true);
    fetchChannelPartnerMonthlyEarnings()
      .then((response) => {
        if (!active) return;
        const nextMonths = response?.data?.data?.months || [];
        setMonths(nextMonths);
        if (!initialMonthRef.current && nextMonths[0]?.month) {
          const firstMonth = nextMonths[0].month;
          initialMonthRef.current = firstMonth;
          setSelectedMonth(firstMonth);
          setSearchParams({ month: firstMonth }, { replace: true });
        }
      })
      .catch((requestError) => {
        if (active) {
          setError(
            getErrorMessage(requestError, "Unable to load available months."),
          );
        }
      })
      .finally(() => {
        if (active) setMonthsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [setSearchParams]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 350);
    return () => window.clearTimeout(timeoutId);
  }, [search]);

  const loadEarnings = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await fetchChannelPartnerEarnings({
        page,
        pageSize,
        search: debouncedSearch,
        month: selectedMonth,
        eventType,
      });
      const data = response?.data?.data || {};
      setEarnings(Array.isArray(data.earnings) ? data.earnings : []);
      setSelectedTotal(data.selected_total || 0);
      setPagination({
        page: data.pagination?.page || page,
        page_size: data.pagination?.page_size || pageSize,
        total_records: data.pagination?.total_records || 0,
        total_pages: data.pagination?.total_pages || 0,
      });
    } catch (requestError) {
      setEarnings([]);
      setSelectedTotal(0);
      setError(
        getErrorMessage(requestError, "Unable to load earning details."),
      );
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, eventType, page, pageSize, selectedMonth]);

  useEffect(() => {
    loadEarnings();
  }, [loadEarnings]);

  const selectedMonthSummary = useMemo(() => {
    if (selectedMonth) {
      return months.find((month) => month.month === selectedMonth) || null;
    }
    return months.reduce(
      (total, month) => ({
        referred_students: (
          total.referred_students + Number(month.referred_students || 0)
        ),
        referral_earnings: (
          total.referral_earnings + Number(month.referral_earnings || 0)
        ),
        completed_counselling: (
          total.completed_counselling
          + Number(month.completed_counselling || 0)
        ),
        counselling_earnings: (
          total.counselling_earnings
          + Number(month.counselling_earnings || 0)
        ),
      }),
      {
        referred_students: 0,
        referral_earnings: 0,
        completed_counselling: 0,
        counselling_earnings: 0,
      },
    );
  }, [months, selectedMonth]);

  const changeMonth = (month) => {
    setSelectedMonth(month);
    setPage(1);
    const nextParams = new URLSearchParams(searchParams);
    if (month) nextParams.set("month", month);
    else nextParams.delete("month");
    setSearchParams(nextParams, { replace: true });
  };

  return (
    <ChannelPartnerDashboardLayout>
      <Box>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/channel-partner-portal/earnings")}
          sx={{
            mb: 1.2,
            px: 0,
            minWidth: 0,
            color: "#292E5C",
            textTransform: "none",
            fontWeight: 650,
            "&:hover": { bgcolor: "transparent", color: "#FFA447" },
          }}
        >
          Back to Earnings
        </Button>

        <Box
          sx={{
            mb: 2.2,
            display: "flex",
            alignItems: { xs: "flex-start", md: "center" },
            justifyContent: "space-between",
            flexDirection: { xs: "column", md: "row" },
            gap: 1.5,
          }}
        >
          <Box>
            <Typography sx={{ fontSize: 23, fontWeight: 750, color: "#171725" }}>
              Earnings Breakdown
            </Typography>
            <Typography sx={{ mt: 0.5, fontSize: 13, color: "#667085" }}>
              {formatMonth(selectedMonth)} · every earning is traceable to its student and source.
            </Typography>
          </Box>
          <Chip
            icon={<CheckCircleOutlineIcon />}
            label="Only active earnings are included in the total"
            sx={{
              minHeight: 36,
              bgcolor: "#ECFDF3",
              color: "#027A48",
              fontWeight: 650,
              borderRadius: "8px",
              "& .MuiChip-icon": { color: "#039855" },
            }}
          />
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              lg: "repeat(3, 1fr)",
            },
            gap: 2,
            mb: 2.2,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              p: 2.2,
              border: "1px solid #D1FADF",
              borderRadius: "10px",
              bgcolor: "#F6FEF9",
            }}
          >
            <Typography sx={{ fontSize: 12, fontWeight: 650, color: "#027A48" }}>
              Active Total
            </Typography>
            <Typography sx={{ mt: 1, fontSize: 28, fontWeight: 750, color: "#05603A" }}>
              {loading ? "--" : formatCurrency(selectedTotal)}
            </Typography>
            <Typography sx={{ mt: 0.45, fontSize: 12, color: "#667085" }}>
              For the filters selected below
            </Typography>
          </Paper>
          <Paper
            elevation={0}
            sx={{
              p: 2.2,
              border: "1px solid #EAECF0",
              borderRadius: "10px",
              bgcolor: "#fff",
            }}
          >
            <Typography sx={{ fontSize: 12, fontWeight: 650, color: "#667085" }}>
              Referred Students
            </Typography>
            <Typography sx={{ mt: 1, fontSize: 28, fontWeight: 750, color: "#171725" }}>
              {monthsLoading ? "--" : (selectedMonthSummary?.referred_students || 0)}
            </Typography>
            <Typography sx={{ mt: 0.45, fontSize: 12, color: "#667085" }}>
              {formatCurrency(selectedMonthSummary?.referral_earnings)} earned
            </Typography>
          </Paper>
          <Paper
            elevation={0}
            sx={{
              p: 2.2,
              border: "1px solid #EAECF0",
              borderRadius: "10px",
              bgcolor: "#fff",
            }}
          >
            <Typography sx={{ fontSize: 12, fontWeight: 650, color: "#667085" }}>
              Completed Counselling
            </Typography>
            <Typography sx={{ mt: 1, fontSize: 28, fontWeight: 750, color: "#171725" }}>
              {monthsLoading ? "--" : (selectedMonthSummary?.completed_counselling || 0)}
            </Typography>
            <Typography sx={{ mt: 0.45, fontSize: 12, color: "#667085" }}>
              {formatCurrency(selectedMonthSummary?.counselling_earnings)} earned
            </Typography>
          </Paper>
        </Box>

        <Paper
          elevation={0}
          sx={{
            border: "1px solid #EAECF0",
            borderRadius: "10px",
            bgcolor: "#fff",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              px: 2.2,
              py: 1.8,
              borderBottom: "1px solid #EAECF0",
              display: "flex",
              alignItems: { xs: "stretch", lg: "center" },
              justifyContent: "space-between",
              flexDirection: { xs: "column", lg: "row" },
              gap: 1.5,
            }}
          >
            <Box>
              <Typography sx={{ fontSize: 17, fontWeight: 700, color: "#171725" }}>
                Transaction Details
              </Typography>
              <Typography sx={{ mt: 0.35, fontSize: 12, color: "#667085" }}>
                Search and filter without leaving this breakdown.
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: { xs: "stretch", sm: "center" },
                flexDirection: { xs: "column", sm: "row" },
                gap: 1,
              }}
            >
              <Select
                size="small"
                displayEmpty
                value={selectedMonth}
                onChange={(event) => changeMonth(event.target.value)}
                disabled={monthsLoading}
                sx={{ minWidth: 165, borderRadius: "8px", fontSize: 13 }}
              >
                <MenuItem value="">All months</MenuItem>
                {months.map((month) => (
                  <MenuItem key={month.month} value={month.month}>
                    {formatMonth(month.month)}
                  </MenuItem>
                ))}
              </Select>
              <Select
                size="small"
                value={eventType}
                onChange={(event) => {
                  setEventType(event.target.value);
                  setPage(1);
                }}
                sx={{ minWidth: 175, borderRadius: "8px", fontSize: 13 }}
              >
                <MenuItem value="">All earning types</MenuItem>
                <MenuItem value="community_referral">Student referrals</MenuItem>
                <MenuItem value="counselling_completion">Completed counselling</MenuItem>
              </Select>
              <TextField
                size="small"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search student or source"
                sx={{
                  width: { xs: "100%", sm: 245 },
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    fontSize: 13,
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 19, color: "#92929D" }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Box>

          {error ? <Alert severity="error" sx={{ m: 2 }}>{error}</Alert> : null}

          <TableContainer sx={{ minHeight: 280 }}>
            <Table sx={{ minWidth: 980 }}>
              <TableHead>
                <TableRow sx={{ bgcolor: "#F9FAFB" }}>
                  {["Date", "Earning Type", "Source", "Student", "Email", "Amount", "Status"].map((label) => (
                    <TableCell
                      key={label}
                      sx={{
                        color: "#667085",
                        fontSize: 12,
                        fontWeight: 650,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 7 }}>
                      <CircularProgress size={28} sx={{ color: "#2D315F" }} />
                    </TableCell>
                  </TableRow>
                ) : earnings.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 7, color: "#667085" }}>
                      No earning records match the selected filters.
                    </TableCell>
                  </TableRow>
                ) : earnings.map((earning) => (
                  <TableRow
                    key={earning.earning_id}
                    hover
                    sx={{ "& td": { borderBottom: "1px solid #EAECF0" } }}
                  >
                    <TableCell sx={{ fontSize: 13, color: "#475467", whiteSpace: "nowrap" }}>
                      {formatDateTime(earning.earned_at)}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={earning.event_label}
                        sx={{
                          borderRadius: "6px",
                          bgcolor: earning.event_type === "community_referral"
                            ? "#ECFDF3"
                            : "#F4F3FF",
                          color: earning.event_type === "community_referral"
                            ? "#027A48"
                            : "#6941C6",
                          fontSize: 11,
                          fontWeight: 650,
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontSize: 13, color: "#344054", fontWeight: 600 }}>
                      {earning.source_name || "--"}
                    </TableCell>
                    <TableCell sx={{ fontSize: 13, color: "#344054" }}>
                      {earning.student_name || "--"}
                    </TableCell>
                    <TableCell sx={{ fontSize: 13, color: "#475467" }}>
                      {earning.student_email || "--"}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: 13,
                        color: earning.status === "earned"
                          ? "#027A48"
                          : earning.status === "pending" ? "#B54708" : "#B42318",
                        fontWeight: 750,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {earning.status === "earned" ? "+" : earning.status === "pending" ? "" : "−"}
                      {formatCurrency(earning.amount)}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={earning.status === "earned"
                          ? "Earned"
                          : earning.status === "pending" ? "Pending test" : "Reversed"}
                        sx={{
                          borderRadius: "6px",
                          bgcolor: earning.status === "earned"
                            ? "#ECFDF3"
                            : earning.status === "pending" ? "#FFF4E8" : "#FEF3F2",
                          color: earning.status === "earned"
                            ? "#027A48"
                            : earning.status === "pending" ? "#B54708" : "#B42318",
                          fontSize: 11,
                          fontWeight: 650,
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={pagination.total_records || 0}
            page={Math.max(0, page - 1)}
            rowsPerPage={pageSize}
            rowsPerPageOptions={[10, 20, 50, 100]}
            onPageChange={(_, nextPage) => setPage(nextPage + 1)}
            onRowsPerPageChange={(event) => {
              setPageSize(Number(event.target.value));
              setPage(1);
            }}
          />
        </Paper>
      </Box>
    </ChannelPartnerDashboardLayout>
  );
};

export default ChannelPartnerEarningDetailsPage;
